import {
  Box,
  Chip,
  Divider,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Stack,
  Typography,
  darken,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CheckRounded, Telegram, WhatsApp } from '@mui/icons-material';
import { Fragment, useState } from 'react';

import {
  StyledButton,
  getTransitionStyle,
} from '../../components/Utils/UIUtils';
import { theme } from '../../theme';
import Visa from '../../assets/payment-methods/visa.png';
import Mastercard from '../../assets/payment-methods/mastercard.png';
import Bitcoin from '../../assets/payment-methods/bitcoin.png';
import BankTransfer from '../../assets/payment-methods/bank-transfer.png';
import i18n from '../../i18n';

const monthlyPrice = {
  en: 14.99,
  es: 13.48,
  fr: 13.48,
  pt: 13.48,
  sv: 158.45,
};

const packagePercentages = {
  1: 0,
  3: 20,
  6: 37,
  12: 55,
};

function calculatePrice(monthlyPrice, totalMonths, savingsPercentage) {
  var price = monthlyPrice * totalMonths * (1 - savingsPercentage / 100);

  return price;
}

const Plan = ({
  plan,
  isMobile,
  translation,
  deviceOptionsPriceFactor,
  deviceOption,
  t,
}) => {
  const currentLanguage =
    i18n.language?.slice(0, 2) ||
    process.env.REACT_APP_DEFAULT_WEBSITE_LANGUAGE;
  const adjustedPrice =
    calculatePrice(
      monthlyPrice[currentLanguage],
      plan.months,
      packagePercentages[plan.months]
    ) * deviceOptionsPriceFactor[deviceOption - 1];
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const paymentMethods = [
    {
      name: 'Mastercard',
      image: Mastercard,
    },
    {
      name: 'Visa',
      image: Visa,
    },
    {
      name: 'Bitcoin',
      image: Bitcoin,
    },
    {
      name: 'Bank Transfer',
      image: BankTransfer,
    },
  ];

  const rawWhatsappMessage = t('PLANS.PAYMENT_MODAL.WHATSAPP_MESSAGE', {
    plan: plan.title,
    deviceOption: deviceOption,
    device:
      deviceOption === 1
        ? t('PLANS.PAYMENT_MODAL.DEVICE')
        : t('PLANS.PAYMENT_MODAL.DEVICES'),
  });
  const encodedWhatsappMessage = encodeURI(rawWhatsappMessage);

  return (
    <Stack
      sx={{
        border: plan.isRecommended ? '1px solid' : 'none',
        borderColor: plan.isRecommended ? 'primary.main' : 'primary.neutral100',
        borderRadius: theme.shape.roundedButtonBorderRadius,
        bgcolor: plan.isRecommended ? 'primary.primarylight1' : 'transparent',
      }}
    >
      <Stack
        direction={'row'}
        justifyContent={'center'}
        alignContent={'center'}
        sx={{
          height: '40px',
          display:
            isMobile || plan.isRecommended
              ? plan.isRecommended
                ? 'flex'
                : 'none'
              : 'flex',
        }}
      >
        {plan.isRecommended && (
          <Typography
            component={'span'}
            sx={{
              fontSize: '16px',
              fontWeight: '500',
              color: 'primary.main',
              my: 'auto',
            }}
          >
            {translation.recommended}
          </Typography>
        )}
      </Stack>
      <Stack
        gap={2}
        sx={{
          px: 2.5,
          py: 3,
          border: plan.isRecommended ? 'none' : '1px solid',
          borderColor: plan.isRecommended
            ? 'primary.main'
            : 'primary.neutral200',
          borderRadius: theme.shape.roundedButtonBorderRadius,
          bgcolor: 'white',
          minWidth: { xs: '300px', md: 'auto' },
          width: { xs: '100%', md: 'auto' },
        }}
      >
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Stack sx={{ mb: 1 }}>
            <Typography
              variant="span"
              sx={{ fontSize: '20px', fontWeight: '600' }}
            >
              {plan.title}
            </Typography>
            <Typography variant="span" noWrap sx={{ color: 'primary.main' }}>
              <Typography
                component="span"
                sx={{
                  fontSize: '14px',
                  fontWeight: '600',
                  position: 'relative',
                  top: '-0.7rem',
                  mr: '0.1rem',
                }}
              >
                {plan.currency}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                }}
              >
                {adjustedPrice.toString()?.split('.')[0]}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: '16px',
                  fontWeight: '600',
                  position: 'relative',
                  top: '-0.3rem',
                  ml: '0.1rem',
                }}
              >
                .{adjustedPrice.toString()?.split('.')[1]?.slice(0, 2)}
              </Typography>
            </Typography>
          </Stack>
          {plan.months > 3 && (
            <Chip
              label={t('HOME_PAGE.SECTION5.SAVE', {
                savings: packagePercentages[plan.months],
              })}
              color="primary"
              variant="outlined"
              sx={{
                border: 'none',
                color: 'primary.main',
                bgcolor: 'primary.primarylight1',
                fontSize: '10px',
                fontWeight: '600',
              }}
            />
          )}
        </Stack>
        <Divider
          sx={{
            width: '30%',
            mx: 'auto',
            borderColor: plan.isRecommended
              ? 'primary.primarylight2'
              : 'primary.neutral200',
          }}
        />
        <List disablePadding>
          {plan.options.map((option, key) => (
            <ListItem disablePadding key={key}>
              <ListItemIcon sx={{ minWidth: 'auto', pr: 1 }}>
                <CheckRounded
                  sx={{
                    fontSize: '20px',
                    color: option.HIGHLIGHTED
                      ? 'primary.main'
                      : 'primary.neutral400',
                    backgroundColor: option.HIGHLIGHTED
                      ? 'primary.primarylight1'
                      : 'transparent',
                    borderRadius: '50%',
                    p: 0.4,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={option.TEXT}
                sx={{
                  '& span': {
                    color: 'primary.neutral800',
                    fontSize: '12px',
                    fontWeight: '400',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
        <StyledButton variant="contained" color="primary" onClick={handleOpen}>
          {plan.button}
        </StyledButton>
      </Stack>
      <Modal
        aria-labelledby="payment-modal-title"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              bgcolor: theme.palette.primary.white,
              boxShadow:
                'rgba(0, 0, 0, 0.2) 0px 11px 15px -7px, rgba(0, 0, 0, 0.14) 0px 24px 38px 3px, rgba(0, 0, 0, 0.12) 0px 9px 46px 8px',
              borderRadius: theme.shape.defaultBorderRadius,
              p: 4,
              overflowY: 'auto',
              margin: 2,
              maxHeight: 'calc(100% - 40px)',
              maxWidth: '600px',
              width: '100%',
              minWidth: { xs: 'auto', md: '600px' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              id="payment-modal-title"
              variant="h3"
              sx={{ fontSize: '24px', fontWeight: '600' }}
            >
              {t('PLANS.PAYMENT_MODAL.TITLE', {
                plan: plan.title,
                adjustedPrice:
                  adjustedPrice.toString()?.split('.')[0] +
                  '.' +
                  adjustedPrice.toString()?.split('.')[1]?.slice(0, 2),
                currency: plan.currency,
              })}
            </Typography>
            <Stack gap={1}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: '400',
                  color: 'text.secondary',
                }}
              >
                {t('PLANS.PAYMENT_MODAL.ACCEPTED_PAYMENT_METHODS')}
              </Typography>
              <Stack
                direction={'row'}
                justifyContent={'center'}
                gap={1}
                sx={{ mb: 1 }}
              >
                {paymentMethods.map((method, i) => (
                  <img
                    key={i}
                    src={method.image}
                    alt={method.name}
                    style={{
                      height: '40px',
                      border: '1px solid',
                      borderRadius: theme.shape.secondaryBorderRadius,
                      borderColor: theme.palette.primary.neutral300,
                    }}
                    loading="lazy"
                  />
                ))}
              </Stack>
            </Stack>
            <Stack gap={1}>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: '600',
                }}
              >
                {t('PLANS.PAYMENT_MODAL.HOW_TO_PAY.TITLE')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  color: 'primary.neutral500',
                  mb: 1,
                }}
              >
                {t('PLANS.PAYMENT_MODAL.HOW_TO_PAY.DESCRIPTION')}
              </Typography>
              <Stack justifyContent={'center'} gap={1}>
                <Stack direction={'row'} justifyContent={'center'} gap={1}>
                  <StyledButton
                    variant="contained"
                    href={`https://wa.me/19292301875/?text=${encodedWhatsappMessage}`}
                    target="_blank"
                    startIcon={<WhatsApp />}
                    sx={{
                      height: '48px',
                      bgcolor: '#25D366',
                      width: '100%',
                      '&:hover': { bgcolor: darken('#25D366', 0.1) },
                      '& .MuiButton-startIcon>*:nth-of-type(1)': {
                        fontSize: '24px',
                      },
                    }}
                  >
                    Whatsapp
                  </StyledButton>
                  <Typography
                    variant="span"
                    sx={{ alignSelf: 'center', color: 'primary.neutral500' }}
                  >
                    {t('GENERAL.OR')}
                  </Typography>
                  <StyledButton
                    variant="contained"
                    href={`https://t.me/slextvofficial`}
                    target="_blank"
                    startIcon={<Telegram />}
                    sx={{
                      height: '48px',
                      bgcolor: '#0088cc',
                      width: '100%',
                      '&:hover': { bgcolor: darken('#0088cc', 0.1) },
                      '& .MuiButton-startIcon>*:nth-of-type(1)': {
                        fontSize: '24px',
                      },
                    }}
                  >
                    Telegram
                  </StyledButton>
                </Stack>
                <Stack
                  direction={'row'}
                  gap={{ xs: 0, sm: 0.5 }}
                  flexWrap={'wrap'}
                >
                  <Typography
                    variant="span"
                    sx={{
                      color: 'primary.neutral500',
                      alignSelf: 'flex-start',
                      mt: 1,
                      fontSize: '15px',
                    }}
                  >
                    {t('PLANS.PAYMENT_MODAL.CONTACT_US_ISSUE')}{' '}
                  </Typography>
                  <Typography
                    variant="span"
                    sx={{
                      color: 'primary.neutral500',
                      alignSelf: 'flex-start',
                      mt: 1,
                      fontSize: '15px',
                    }}
                  >
                    {t('PLANS.PAYMENT_MODAL.CONTACT_US_AT')}{' '}
                    <a
                      href={'tel:' + process.env.REACT_APP_SUPPORT_PHONE}
                      style={{ color: theme.palette.primary.main }}
                    >
                      {process.env.REACT_APP_SUPPORT_PHONE}
                    </a>
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Fade>
      </Modal>
    </Stack>
  );
};

export default function Plans() {
  const { t } = useTranslation();
  const plans = [
    {
      title: t('HOME_PAGE.SECTION5.PLANS.0.TITLE'),
      price: t('HOME_PAGE.SECTION5.PLANS.0.PRICE'),
      options:
        t('HOME_PAGE.SECTION5.PLANS.0.OPTIONS', { returnObjects: true }) || [],
      button: t('HOME_PAGE.SECTION5.PLANS.0.BUTTON'),
      isRecommended: false,
      currency: t('GENERAL.CURRENCY_SYMBOL'),
      months: 1,
    },
    {
      title: t('HOME_PAGE.SECTION5.PLANS.1.TITLE'),
      price: t('HOME_PAGE.SECTION5.PLANS.1.PRICE'),
      options:
        t('HOME_PAGE.SECTION5.PLANS.1.OPTIONS', { returnObjects: true }) || [],
      button: t('HOME_PAGE.SECTION5.PLANS.1.BUTTON'),
      isRecommended: false,
      currency: t('GENERAL.CURRENCY_SYMBOL'),
      months: 3,
    },
    {
      title: t('HOME_PAGE.SECTION5.PLANS.2.TITLE'),
      price: t('HOME_PAGE.SECTION5.PLANS.2.PRICE'),
      options:
        t('HOME_PAGE.SECTION5.PLANS.2.OPTIONS', { returnObjects: true }) || [],
      button: t('HOME_PAGE.SECTION5.PLANS.2.BUTTON'),
      isRecommended: false,
      currency: t('GENERAL.CURRENCY_SYMBOL'),
      months: 6,
    },
    {
      title: t('HOME_PAGE.SECTION5.PLANS.3.TITLE'),
      price: t('HOME_PAGE.SECTION5.PLANS.3.PRICE'),
      options:
        t('HOME_PAGE.SECTION5.PLANS.3.OPTIONS', { returnObjects: true }) || [],
      button: t('HOME_PAGE.SECTION5.PLANS.3.BUTTON'),
      isRecommended: true,
      currency: t('GENERAL.CURRENCY_SYMBOL'),
      months: 12,
    },
  ];

  const isMobile = useMediaQuery('(max-width: 600px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const shouldScrollHorizontally = isTablet;

  const [deviceOption, setDeviceOption] = useState(1);
  const deviceOptionsPriceFactor = [1, 1.9, 2.8];
  const deviceMaxOptions = t('HOME_PAGE.SECTION5.DEVICE_MAX_OPTIONS', {
    returnObjects: true,
  });

  const [bgOffset, setBgOffset] = useState(1.1);

  const slideBg = (n) => {
    const newBgOffset = n === 0 ? 1.1 : n === 1 ? 33.33 : 65.5;
    setBgOffset(newBgOffset);
  };
  return (
    <Stack>
      <Stack
        direction={'row'}
        sx={{
          bgcolor: 'primary.neutral100',
          width: { xs: '90%', sm: 'fit-content' },
          my: 2,
          mx: 'auto',
          borderRadius: theme.shape.secondaryBorderRadius,
          position: 'relative',
          '&::before': {
            content: '""',
            width: `${100 / deviceMaxOptions.length}%`,
            height: '80%',
            position: 'absolute',
            top: '10%',
            left: bgOffset + '%',
            background: 'white',
            borderRadius: theme.shape.secondaryBorderRadius,
            ...getTransitionStyle(theme, ['left']),
          },
        }}
        justifyContent={'center'}
      >
        {deviceMaxOptions.map((option, i) => (
          <Fragment key={i}>
            <StyledButton
              color="primary"
              sx={{
                color:
                  deviceOption === i + 1 ? 'primary.main' : 'text.secondary',
                bgcolor: 'transparent',
                fontSize: '14px',
                padding: '4px 16px',
                height: '40px',
                width: '128px',
                whiteSpace: 'nowrap',
                '&:hover': {
                  color:
                    deviceOption === i + 1 ? 'primary.main' : 'text.secondary',
                  bgcolor: 'initial',
                },
              }}
              onClick={() => {
                setDeviceOption(i + 1);
                slideBg(i);
              }}
            >
              {option}
            </StyledButton>
          </Fragment>
        ))}
      </Stack>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        sx={{
          mt: '16px',
          width: '100%',
          p: 1,
          overflowX: shouldScrollHorizontally ? 'auto' : 'visible',
        }}
      >
        {plans.map((plan, i) => (
          <Plan
            key={i}
            plan={plan}
            isMobile={isMobile}
            t={t}
            translation={{ recommended: t('HOME_PAGE.SECTION5.RECOMMENDED') }}
            deviceOptionsPriceFactor={deviceOptionsPriceFactor}
            deviceOption={deviceOption}
          />
        ))}
      </Stack>
    </Stack>
  );
}
